import { createVeloCLIServer } from '@wix/editor-velo-cli-comm-api';
import { openBrowser } from '@wix/user-browser';
import { asyncExitHook } from 'exit-hook';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { ErrorViewer } from '@wix/cli-error-reporting';
import { writeCrashReport } from '@wix/cli-debug-log';
import { getDebugLogFilePath } from '@wix/cli-core-definitions';
import type { CommandServices } from '@wix/cli-command-contract';
import { createElement } from 'react';
import { renderSync } from '@wix/cli-ui-kit';
import { createHttpClient, isHttpError } from '@wix/cli-http-client';
import type { Connection } from '@wix/cli-tunnel';
import { TunnelClient, launchTunnel } from '@wix/cli-tunnel';
import { getDevEditorBaseUrl } from '../environment.js';
import { createMutableGridApp } from '../gridapp/createGridApp.js';
import { startGridAppUpdater } from '../gridapp/startGridAppUpdater.js';
import { KeyWatcher } from '../key-watcher.js';
import type { ProjectModel } from '../model.js';
import { saveProjectModel } from '../model.js';
import { createLogger } from '../logger.js';
import { sitesBiEvents } from '../bi.js';
import { createI18nT } from '../i18n.js';
import { authenticate } from '../authentication.js';
import { syncAllToRevision } from './sync-all-to-revision.js';

export async function dev(
  initialModel: ProjectModel,
  { biLogger, errorReporter }: CommandServices,
  {
    https = false,
    tunnel: useTunnel = false,
  }: {
    https?: boolean;
    tunnel?: boolean;
  }
) {
  let model = initialModel;
  const authState = await authenticate({
    metaSiteId: model.metaSiteId,
    biLogger,
    errorReporter,
  });

  const t = createI18nT();
  const logger = createLogger({ t });
  const biEvents = sitesBiEvents(biLogger);

  const server = createVeloCLIServer({ https });

  let gridAppId: string;
  try {
    gridAppId = await createMutableGridApp(model.projectFolder, {
      authState,
    });
  } catch (error) {
    const message = isHttpError(error)
      ? error.response?.data?.message
      : undefined;

    throw new CliError({
      code: CliErrorCode.FailedToCreateGridApp({
        error: message,
      }),
      cause: error,
    });
  }

  await syncAllToRevision(model, { authState }, logger);

  const { close } = startGridAppUpdater(
    model,
    gridAppId,
    {
      authState,
      onError(error) {
        logger.logNewLine();
        errorReporter.reportError(error);
        writeCrashReport(getDebugLogFilePath(model.projectFolder), error);
        renderSync(createElement(ErrorViewer, { error }));
      },
    },
    logger,
    biEvents
  );

  const onServerDisconnected = (devEditorUrl: string) => {
    const message = logger.logNoOpenedTab(devEditorUrl);
    biEvents.messageDisplayed({ model, message });
  };

  const onSyncDevEditorChanges = async (revision: string) => {
    if (revision !== model.revision) {
      const newModel = { ...model, revision };

      await saveProjectModel(newModel);
      const message = logger.logUpdatedLocalCodeToRevision({
        revision: newModel.revision,
      });
      biEvents.messageDisplayed({ message, model });

      await syncAllToRevision(newModel, { authState }, logger);

      model = newModel;
    }
  };

  const openLocalEditor = async (serverPort: number) => {
    biEvents.userResponse({
      action: 'Browser opened',
      message: 'Opening the Local Editor',
      model,
    });

    const devEditorUrl = getDevEditorUrl(serverPort);

    if (https) {
      await openBrowser(new URL(`https://localhost:${serverPort}`).href);
    } else {
      await openBrowser(devEditorUrl);
    }
  };

  let keyWatcher: KeyWatcher | undefined;
  let tunnel: Connection | undefined;

  const getDevEditorUrl = (serverPort: number) => {
    const url = new URL(model.metaSiteId, getDevEditorBaseUrl());

    if (tunnel) {
      url.searchParams.append('localEditorDestination', tunnel.domain);
    } else {
      url.searchParams.append('localPort', serverPort.toString());
    }

    url.searchParams.append('secureSocket', https.toString());

    return url.toString();
  };

  try {
    const { port: serverPort } = await server.serve(gridAppId, {
      getDevEditorUrl,
    });

    if (useTunnel) {
      const httpClient = createHttpClient({
        type: 'backoffice',
        getAppToken: authState.getAccessToken,
      });
      const tunnelClient = new TunnelClient(httpClient);
      tunnel = await launchTunnel(serverPort, tunnelClient);
    }

    const devEditorUrl = getDevEditorUrl(serverPort);

    server.on('disconnected', () => onServerDisconnected(devEditorUrl));
    server.on('syncDevEditorChanges', onSyncDevEditorChanges);

    keyWatcher = new KeyWatcher();

    logger.logOpeningBrowser(https);

    await openLocalEditor(serverPort);
    const { ctrlCPromise } = keyWatcher.watch();
    keyWatcher.on('e', () => openLocalEditor(serverPort));

    asyncExitHook(
      async () => {
        await server.close();
        tunnel?.close();
      },
      { wait: 500 }
    );
    await ctrlCPromise;
  } finally {
    keyWatcher?.unwatch();
    await Promise.all([server.close(), close(), tunnel?.close()]);
  }
}
